import "./SceneManagement.css";
import { Button, Card, Checkbox, Col, Divider, Modal, Row, Select, Tooltip, Upload } from "antd";
import { QuestionCircleOutlined } from '@ant-design/icons';
import { UploadOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import FramePickerModal from "./FramePickerModal/FramePickerModal";

const sceneSetUpOptions = [
  { value: "first-last-frame", label: "First Frame & Last Frame" },
  { value: "image-reference", label: "Image Reference" },
];

const DEFAULT_SCENE = {
  setUpValue: "first-last-frame",
  durationValue: 8,
  // applyBrand: true,
  applyBrand: false,
  firstFrame: null,
  lastFrame: null,
  refs: [null, null, null],
};

const makePreview = (file) => ({
  file,
  displayUrl: URL.createObjectURL(file),
  name: file.name,
});

export default function SceneManagement({
  scenes = [],
  onNext,
  generatedImages,
  storyboardId,
  selectedConceptIds,
  sceneState,
  setSceneState,
}) {
  const desc = "Configure reference for each scene";

  const [frameModal, setFrameModal] = useState({
    open: false,
    which: null,
    sceneId: null,
  });

  const [preview, setPreview] = useState({ open: false, src: "", title: "" });
  const closePreview = () => setPreview({ open: false, src: "", title: "" });

  const revokeIfBlob = (url) => {
    if (typeof url === "string" && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  };

  const parseDuration = (timeRange) => {
    if (!timeRange) return null;
    const match = timeRange.match(/(\d+)s$/);
    return match ? parseInt(match[1], 10) : null;
  };

  const getSceneDurationOptions = (suggestedSeconds) => [
    { value: 4, label: suggestedSeconds === 4 ? "4s (AI Suggested)" : "4s" },
    { value: 6, label: suggestedSeconds === 6 ? "6s (AI Suggested)" : "6s" },
    { value: 8, label: suggestedSeconds === 8 ? "8s (AI Suggested)" : "8s" },
  ];

  useEffect(() => {
    setSceneState((prev) => {
      const next = { ...prev };

      scenes.forEach((sc) => {
        if (!next[sc.id]) {
          const suggested = parseDuration(sc.timeRange);
          next[sc.id] = {
            ...DEFAULT_SCENE,
            durationValue: suggested ?? DEFAULT_SCENE.durationValue,
          };
        }
      });

      const sceneIds = new Set(scenes.map((s) => String(s.id)));
      for (const id of Object.keys(next)) {
        if (!sceneIds.has(String(id))) {
          const st = next[id];

          revokeIfBlob(st?.firstFrame?.displayUrl || st?.firstFrame?.url);
          revokeIfBlob(st?.lastFrame?.displayUrl || st?.lastFrame?.url);
          st?.refs?.forEach((r) => revokeIfBlob(r?.displayUrl || r?.url));
          delete next[id];
        }
      }

      return next;
    });
  }, [scenes]);

  const updateScene = (id, patch) => {
    setSceneState((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? DEFAULT_SCENE), ...patch },
    }));
  };

  const removeSceneFrame = (sceneId, which) => {
    setSceneState((prev) => {
      const prevScene = prev[sceneId] ?? DEFAULT_SCENE;
      const old = prevScene[which];
      revokeIfBlob(old?.displayUrl);
      revokeIfBlob(old?.url);

      return {
        ...prev,
        [sceneId]: { ...prevScene, [which]: null },
      };
    });
  };

  const setSceneRef = (sceneId, index, file) => {
    setSceneState((prev) => {
      const prevScene = prev[sceneId] ?? DEFAULT_SCENE;
      const refs = [...(prevScene.refs ?? [null, null, null])];

      revokeIfBlob(refs[index]?.displayUrl || refs[index]?.url);

      refs[index] = makePreview(file);

      return {
        ...prev,
        [sceneId]: { ...prevScene, refs },
      };
    });

    return false;
  };

  const removeSceneRef = (sceneId, index) => {
    setSceneState((prev) => {
      const prevScene = prev[sceneId] ?? DEFAULT_SCENE;
      const refs = [...(prevScene.refs ?? [null, null, null])];

      revokeIfBlob(refs[index]?.displayUrl || refs[index]?.url);
      refs[index] = null;

      return { ...prev, [sceneId]: { ...prevScene, refs } };
    });
  };

  return (
    <div className="checkbox-container-sm">
      <div className="content-title-container">
        <div className="content-title">
          {desc}
          <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_SCENE_MANAGEMENT}>
              <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
          </Tooltip>
        </div>
        <Divider className="content-title-divider" />
      </div>

      <FramePickerModal
        open={frameModal.open}
        which={frameModal.which}
        sceneId={frameModal.sceneId}
        storyboardId={storyboardId}
        generatedImagesPrompt={generatedImages}
        selectedConceptIds={selectedConceptIds}
        onChange={(v) => setFrameModal((p) => ({ ...p, value: v }))}
        onCancel={() => setFrameModal((p) => ({ ...p, open: false }))}
        onOk={(result) => {
          updateScene(frameModal.sceneId, {
            [frameModal.which]: {
              mode: result.mode,
              type: result.type,
              uri: result.uri,
              displayUrl: result.displayUrl || "",
              name: result.uri?.split("/").pop() || "selected-image",
            },
          });

          setFrameModal((p) => ({ ...p, open: false }));
        }}
      />

      <Row className="scenes-container" gutter={[0, 8]}>
        {scenes.map((it, idx) => {
          const s = sceneState[it.id] ?? DEFAULT_SCENE;

          const sceneText =
            it.description ?? it.sceneDescription ?? "(No description)";

          const firstSrc = s.firstFrame?.displayUrl || s.firstFrame?.url || "";
          const lastSrc = s.lastFrame?.displayUrl || s.lastFrame?.url || "";

          const suggested = parseDuration(it.timeRange);

          return (
            <Col key={it.id} span={24} >
              <div className="subheading">
                Scene {it.sceneNumber ?? idx + 1} {it.name ? `— ${it.name}` : ""}
              </div>

              <Card className="pick-card">
                <div className="pick-desc">{sceneText}</div>

                <div className="scene-row">
                  <div className="scene-settings-container">
                    <div className="title">Scene Set Up</div>
                    <Select
                      className="options"
                      value={s.setUpValue}
                      onChange={(value) => updateScene(it.id, { setUpValue: value })}
                      options={sceneSetUpOptions}
                    />
                  </div>

                  <div className="scene-settings-container">
                    <div className="title">Scene Duration</div>
                    <Select
                      className="options"
                      value={s.durationValue}
                      onChange={(value) => updateScene(it.id, { durationValue: value })}
                      options={getSceneDurationOptions(suggested)}
                    />
                  </div>

                  <Checkbox
                    checked={s.applyBrand}
                    onChange={(e) => updateScene(it.id, { applyBrand: e.target.checked })}
                    className="title checkbox"
                  >
                    Apply Brand Guidelines
                  </Checkbox>
                </div>

                {s.setUpValue === "first-last-frame" ? (
                  <div className="scene-row">
                    {/* First Frame */}
                    <div className="scene-settings-container">
                      <div className="title">First Frame</div>

                      {s.firstFrame ? (
                        <div className="slot-filled">
                          <img className="slot-img" src={firstSrc} alt={s.firstFrame.name} />
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            className="slot-preview"
                            onClick={() =>
                              setPreview({ open: true, src: firstSrc, title: "First Frame" })
                            }
                            disabled={!firstSrc}
                          />
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            className="slot-remove"
                            onClick={() => removeSceneFrame(it.id, "firstFrame")}
                          />
                        </div>
                      ) : (
                        <Button
                          icon={<UploadOutlined />}
                          onClick={() =>
                            setFrameModal({
                              open: true,
                              sceneId: it.id,
                              which: "firstFrame",
                            })
                          }
                        >
                          Click to Choose
                        </Button>
                      )}
                    </div>

                    {/* Last Frame */}
                    <div className="scene-settings-container">
                      <div className="title">Last Frame</div>

                      {s.lastFrame ? (
                        <div className="slot-filled">
                          <img className="slot-img" src={lastSrc} alt={s.lastFrame.name} />
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            className="slot-preview"
                            onClick={() =>
                              setPreview({ open: true, src: lastSrc, title: "Last Frame" })
                            }
                            disabled={!lastSrc}
                          />
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            className="slot-remove"
                            onClick={() => removeSceneFrame(it.id, "lastFrame")}
                          />
                        </div>
                      ) : (
                        <Button
                          icon={<UploadOutlined />}
                          onClick={() =>
                            setFrameModal({
                              open: true,
                              sceneId: it.id,
                              which: "lastFrame",
                            })
                          }
                        >
                          Click to Choose
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="three-slots">
                    {[0, 1, 2].map((i) => {
                      const slot = s.refs?.[i];

                      const refSrc = slot?.displayUrl || slot?.url || "";

                      return (
                        <div key={i} className="slot">
                          {slot ? (
                            <div className="slot-filled">
                              <img className="slot-img" src={refSrc} alt={slot.name} />
                              <Button
                                type="text"
                                icon={<EyeOutlined />}
                                className="slot-preview"
                                onClick={() => setPreview({ open: true, src: refSrc, title: `Reference ${i + 1}` })}
                                disabled={!refSrc}
                              />
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                className="slot-remove"
                                onClick={() => removeSceneRef(it.id, i)}
                              />
                            </div>
                          ) : (
                            <Upload
                              accept="image/*"
                              showUploadList={false}
                              beforeUpload={(file) => setSceneRef(it.id, i, file)}
                            >
                              <Button icon={<UploadOutlined />}>Upload</Button>
                            </Upload>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      <div className="back-next-button-sm">
        <Button htmlType="button" onClick={onNext}>
          Next
        </Button>
      </div>

      <Modal
        open={preview.open}
        title={preview.title}
        footer={null}
        onCancel={closePreview}
        centered
        width="70vw"
        styles={{ body: { padding: 0 } }}
        destroyOnHidden
      >
        <img
          src={preview.src}
          alt="Preview"
          style={{ width: "100%", display: "block" }}
        />
      </Modal>

    </div>
  );
}
